import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { tenants } from '../lib/data';
import ProfileCard from '../components/ProfileCard';

export default function Search({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [filter, setFilter] = useState('all');
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);

  const q = (searchParams.get('q') || '').toLowerCase();
  const isManager = user?.role === 'manager';

  useEffect(() => {
    async function loadManagers() {
      const { data, error } = await supabase.from('managers').select('*');
      if (!error) setManagers(data || []);
      setLoading(false);
    }
    loadManagers();
  }, []);

  // Tenants can only see managers. Managers can see everyone.
  const allProfiles = isManager
    ? [...managers.map(m => ({ ...m, type: 'manager' })), ...tenants]
    : [...managers.map(m => ({ ...m, type: 'manager' }))];

  // If filter is set to tenants and user is not a manager, block it
  const activeFilter = !isManager && filter === 'tenant' ? 'manager' : filter;

  const results = allProfiles.filter(p => {
    const matchesFilter = activeFilter === 'all' || p.type === activeFilter;
    if (!q) return matchesFilter;
    const searchStr = [p.name, p.address, p.neighborhood].filter(Boolean).join(' ').toLowerCase();
    return matchesFilter && searchStr.includes(q);
  });

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams(query ? { q: query } : {});
  };

  return (
    <div className="page-wrap" style={{ background: '#F5EFE3' }}>
      <div style={{ padding: '18px 16px 14px' }}>
        <div className="container" style={{ padding: 0 }}>
          <form onSubmit={handleSearch} style={{ position: 'relative' }}>
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#6B6358" strokeWidth={2}
              style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
            </svg>
            <input value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search managers, tenants, addresses..."
              style={{ width: '100%', padding: '12px 44px 12px 38px', borderRadius: 10, border: '1.5px solid rgba(184,137,58,0.30)', background: '#FAF6EC', color: '#2C2C2C', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            {query && (
              <button type="button" onClick={() => { setQuery(''); setSearchParams({}); }}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#6B6358', fontSize: 18, cursor: 'pointer' }}>×</button>
            )}
          </form>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {[['all', 'All'], ['manager', 'Managers'], ...(isManager ? [['tenant', 'Tenants']] : [])].map(([val, label]) => (
              <button key={val} onClick={() => setFilter(val)}
                style={{
                  padding: '5px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                  background: filter === val ? '#0F2C4E' : '#FAF6EC',
                  color: filter === val ? '#fff' : '#0F2C4E',
                  border: filter === val ? '1px solid #0F2C4E' : '1px solid rgba(184,137,58,0.30)',
                  fontWeight: filter === val ? 600 : 500,
                }}>
                {label}
              </button>
            ))}
          </div>
          {!isManager && (
            <div style={{ marginTop: 10, fontSize: 11, color: '#6B6358', background: '#FAF6EC', border: '1px solid rgba(184,137,58,0.22)', borderRadius: 8, padding: '6px 12px' }}>
              🔒 Tenant profiles are only visible to verified managers
            </div>
          )}
        </div>
      </div>

      <div className="container" style={{ paddingTop: 8, paddingBottom: 32 }}>
        {loading
          ? <div style={{ textAlign: 'center', padding: 32, color: '#6B6358', fontSize: 14 }}>Loading...</div>
          : <>
              <p style={{ fontSize: 13, color: '#6B6358', marginBottom: 16 }}>
                {results.length} result{results.length !== 1 ? 's' : ''}{q ? ' for "' + q + '"' : ''}
              </p>
              {results.length > 0
                ? results.map(p => <ProfileCard key={p.id} profile={p} />)
                : <div style={{ textAlign: 'center', padding: '48px 0', color: '#6B6358' }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
                    <div style={{ fontSize: 15, fontWeight: 500, color: '#0F2C4E', marginBottom: 6 }}>No results found</div>
                    <div style={{ fontSize: 13 }}>Try a different name or neighborhood</div>
                  </div>
              }
            </>
        }
      </div>
    </div>
  );
}
